/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { FC, ReactElement, useState, useRef, ChangeEvent, ReactNode } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { FormikValues } from 'formik'
import _ from 'lodash'

import { makeStyles } from '@material-ui/core/styles'
import { AppBar, Tabs, Tab, Box } from '@material-ui/core'
import { inject, observer } from 'mobx-react'

import { Prospects as ProspectsStore } from '../../../../stores/prospects'
import { Settings as SettingsStore } from '../../../../stores/settings'
import Spinner from '../../../Spinner'
import TouchesForm from './TouchesForm'
import './styles.scss'

interface TabPanelProps {
  children: ReactNode
  index: number
  value: number
}
const TabPanelType = (props: TabPanelProps): JSX.Element => {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-type-${index}`}
      aria-labelledby={`scrollable-auto-tab-type-${index}`}
      {...other}
      className="tabpanel"
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

const TabPanelCampaign = (props: TabPanelProps): JSX.Element => {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-campaign-${index}`}
      aria-labelledby={`scrollable-auto-tab-campaign-${index}`}
      {...other}
      className="tabpanel"
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

const a11yPropsType = (index: number): { id: string; 'aria-controls': string } => {
  return {
    id: `scrollable-auto-tab-type-${index}`,
    'aria-controls': `scrollable-auto-tabpanel-type-${index}`,
  }
}

const a11yPropsCampaign = (index: number): { id: string; 'aria-controls': string } => {
  return {
    id: `scrollable-auto-tab-campaign-${index}`,
    'aria-controls': `scrollable-auto-tabpanel-campaign-${index}`,
  }
}

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
    width: '100%',
  },
  tabs: {
    '& .MuiTab-root': {
      flex: 1,
      minWidth: 'unset',
      minHeight: 'unset',
      padding: '4px 12px',
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#27a9e0',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: '#27a9e0',
    },
  },
  appBar: {
    '& .MuiTabs-root': {
      minHeight: 'unset',
    },
  },
}))

interface Props extends RouteComponentProps {
  prospects?: ProspectsStore
  settings?: SettingsStore
}

const Templates: FC<Props> = props => {
  const [currentCampaignIndex, setCurrentCampaignIndex] = useState(0)
  const [currentType, setCurrentType] = useState(0)
  const { prospects } = props
  const classes = useStyles()

  const handleTypeChange = (event: ChangeEvent<{}>, newValue: number): void => {
    setCurrentType(newValue)
  }

  const handleCampaignChange = (event: ChangeEvent<{}>, newValue: number): void => {
    setCurrentCampaignIndex(newValue)
  }

  const firstTouchRef = useRef<FormikValues>(null)
  const followUpRef = useRef<FormikValues>(null)
  const nurtureRef = useRef<FormikValues>(null)
  const sortedFirstTouchOptions = _.keys(prospects?.masters?.firstTouch).sort()

  let content: ReactElement | null = null

  content = prospects?.isLoading ? (
    <div className="touches w-100">
      <Spinner />
    </div>
  ) : (
    <div className="touches w-100 p-4 overflow-auto">
      <div className={classes.root}>
        <AppBar position="static" color="default" className={classes.appBar}>
          <Tabs
            value={currentType}
            onChange={handleTypeChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable auto tabs example"
            className={classes.tabs}
          >
            <Tab label="First Touch" key="firstTouch" {...a11yPropsType(0)} />
            <Tab label="Follow Up" key="followUp" {...a11yPropsType(1)} />
            <Tab label="Nurture" key="nurture" {...a11yPropsType(2)} />
          </Tabs>
        </AppBar>
        <TabPanelType value={currentType} index={0}>
          <AppBar position="static" color="default" className={classes.appBar}>
            <Tabs
              value={currentCampaignIndex}
              onChange={handleCampaignChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              aria-label="scrollable auto tabs example"
              className={classes.tabs}
            >
              {sortedFirstTouchOptions.map((campaign, index) => (
                <Tab label={campaign} key={campaign} {...a11yPropsCampaign(index)} />
              ))}
            </Tabs>
          </AppBar>
          {sortedFirstTouchOptions.map((campaign, index) => (
            <TabPanelCampaign value={currentCampaignIndex} index={index} key={`tab1 - ${campaign}`}>
              <TouchesForm
                maneuverType="first touch"
                currentCampaign={campaign}
                templates={prospects?.masters?.firstTouch[campaign]}
                daysBetween={prospects?.masters?.daysBetween.firstTouch[campaign]}
                innerRef={firstTouchRef}
              />
            </TabPanelCampaign>
          ))}
        </TabPanelType>
        <TabPanelType value={currentType} index={1}>
          <AppBar position="static" color="default" className={classes.appBar}>
            <Tabs
              value={currentCampaignIndex}
              onChange={handleCampaignChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              aria-label="scrollable auto tabs example"
              className={classes.tabs}
            >
              {sortedFirstTouchOptions.map((campaign, index) => (
                <Tab label={campaign} key={campaign} {...a11yPropsCampaign(index)} />
              ))}
            </Tabs>
          </AppBar>
          {sortedFirstTouchOptions.map((campaign, index) => (
            <TabPanelCampaign value={currentCampaignIndex} index={index} key={`tab2 - ${campaign}`}>
              <TouchesForm
                maneuverType="follow up"
                currentCampaign={campaign}
                templates={prospects?.masters?.followUp[campaign]}
                daysBetween={prospects?.masters?.daysBetween.followUp[campaign]}
                innerRef={followUpRef}
              />
            </TabPanelCampaign>
          ))}
        </TabPanelType>
        <TabPanelType value={currentType} index={2}>
          <AppBar position="static" color="default" className={classes.appBar}>
            <Tabs
              value={currentCampaignIndex}
              onChange={handleCampaignChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              aria-label="scrollable auto tabs example"
              className={classes.tabs}
            >
              {sortedFirstTouchOptions.map((campaign, index) => (
                <Tab label={campaign} key={campaign} {...a11yPropsCampaign(index)} />
              ))}
            </Tabs>
          </AppBar>
          {sortedFirstTouchOptions.map((campaign, index) => (
            <TabPanelCampaign value={currentCampaignIndex} index={index} key={`tab3 - ${campaign}`}>
              <TouchesForm
                maneuverType="nurture"
                currentCampaign={campaign}
                templates={prospects?.masters?.nurture[campaign]}
                daysBetween={prospects?.masters?.daysBetween.nurture[campaign]}
                innerRef={nurtureRef}
              />
            </TabPanelCampaign>
          ))}
        </TabPanelType>
      </div>
    </div>
  )

  return content
}

export default inject('settings')(observer(Templates))
